import { getInternalReturnUrl } from "@/lib/internalReturnUrl";

export { getInternalReturnUrl };

export const ASSUMED_USER_STORAGE_KEY = "m360.assumed-user";
export const ASSUMED_USER_CLEARED_EVENT = "m360.assumed-user-cleared";

export type AssumedUser = {
  id: string;
  name: string;
  email: string;
  photo?: string | null;
  department?: string | null;
  returnUrl: string;
};

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;
const isOptionalString = (value: unknown): value is string | null | undefined =>
  value === undefined || value === null || typeof value === "string";

const isAssumedUser = (value: unknown): value is AssumedUser => {
  if (typeof value !== "object" || value === null) return false;

  const assumedUser = value as Record<string, unknown>;

  return (
    isNonEmptyString(assumedUser.id) &&
    isNonEmptyString(assumedUser.name) &&
    isNonEmptyString(assumedUser.email) &&
    getInternalReturnUrl(assumedUser.returnUrl) === assumedUser.returnUrl &&
    isOptionalString(assumedUser.photo) &&
    isOptionalString(assumedUser.department)
  );
};

const isStoredAssumedUser = (value: unknown): value is AssumedUser & { authenticatedUserSub: string } => {
  if (!isAssumedUser(value)) return false;

  return isNonEmptyString((value as Record<string, unknown>).authenticatedUserSub);
};

export function readAssumedUser(authenticatedUserSub: string): AssumedUser | null {
  try {
    const stored = window.localStorage.getItem(ASSUMED_USER_STORAGE_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (!isStoredAssumedUser(parsed)) {
      clearAssumedUserStorage({ notify: false });
      return null;
    }
    if (parsed.authenticatedUserSub !== authenticatedUserSub) {
      clearAssumedUserStorage();
      return null;
    }

    const { authenticatedUserSub: _, ...assumedUser } = parsed;
    return assumedUser;
  } catch {
    return null;
  }
}

export function persistAssumedUser(assumedUser: AssumedUser, authenticatedUserSub: string): void {
  try {
    window.localStorage.setItem(
      ASSUMED_USER_STORAGE_KEY,
      JSON.stringify({
        ...assumedUser,
        returnUrl: getInternalReturnUrl(assumedUser.returnUrl),
        authenticatedUserSub
      })
    );
  } catch {
    // Assumed-user state is also kept in memory, so storage availability should not prevent a session from starting.
  }
}

export function clearAssumedUserStorage({ notify = true }: { notify?: boolean } = {}): void {
  let hadStoredSession = false;
  let storageReadSucceeded = false;

  try {
    hadStoredSession = window.localStorage.getItem(ASSUMED_USER_STORAGE_KEY) !== null;
    storageReadSucceeded = true;
    window.localStorage.removeItem(ASSUMED_USER_STORAGE_KEY);
  } catch {
    // In-memory state must still be cleared when browser storage is unavailable.
  }

  if (notify && (hadStoredSession || !storageReadSucceeded)) {
    window.dispatchEvent(new Event(ASSUMED_USER_CLEARED_EVENT));
  }
}
